import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import FileUploadField from './FileUploadField';
import { useAuth, USER_ROLES } from '../../auth/AuthContext';
import { authService } from '../../services/apiService';
import { getProfileDisplayEmail } from '../../utils/displayName';
import GarageServicePreview from '../auth/GarageServicePreview';
import { previewGarageServices } from '../../utils/garageServiceExpansion';
import { normalizeGarageTier } from '../../constants/garageServiceClusters';
import { FaStore, FaMapMarkerAlt, FaWrench, FaUniversity } from 'react-icons/fa';

const STEPS = [
  { label: 'Garage Identity', icon: FaStore, desc: 'Garage basic information' },
  { label: 'Location & Hours', icon: FaMapMarkerAlt, desc: 'Location and operating hours' },
  { label: 'Services', icon: FaWrench, desc: 'Core services offered' },
  { label: 'Bank & Agreement', icon: FaUniversity, desc: 'Banking and agreement details' }
];
const DRAFT_KEY = 'onboarding_garage_draft';

const Wrap = styled.div`
  height: 100vh;
  display: flex;
  background: #f0f4f8;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 380px;
  background: #1a2332;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 30%, rgba(255,255,255,0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(255,255,255,0.03) 0%, transparent 50%);
    pointer-events: none;
    opacity: 0.6;
  }
  
  @media (max-width: 1024px) {
    width: 320px;
    padding: 32px 24px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
  
  .logo-s {
    width: 56px;
    height: 56px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .s-letter {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &::before {
        content: 'S';
        font-size: 48px;
        font-weight: 900;
        color: #ffffff;
        letter-spacing: -3px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
        text-shadow: 0 2px 8px rgba(0,0,0,0.4), 0 0 15px rgba(255,255,255,0.15);
        position: relative;
        z-index: 2;
      }
      
      &::after {
        content: '';
        position: absolute;
        width: 52px;
        height: 52px;
        border: 2px solid rgba(255,255,255,0.25);
        border-radius: 12px;
        transform: rotate(-10deg);
        z-index: 1;
        background: rgba(255,255,255,0.08);
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      }
    }
    
    .s-accent {
      position: absolute;
      width: 8px;
      height: 8px;
      background: rgba(255,255,255,0.7);
      border-radius: 50%;
      top: 8px;
      right: 8px;
      z-index: 3;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
    
    .s-decoration {
      position: absolute;
      width: 4px;
      height: 20px;
      background: rgba(255,255,255,0.3);
      border-radius: 2px;
      bottom: -8px;
      left: 50%;
      transform: translateX(-50%) rotate(15deg);
      z-index: 1;
    }
  }
`;

const ProgressSteps = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  z-index: 1;
`;

const ProgressStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  position: relative;
  padding-bottom: 32px;
  
  ${props => props.active && `
    .step-icon {
      background: #64b5f6;
      border-color: #ffffff;
      color: #ffffff;
      box-shadow: 0 2px 8px rgba(100, 181, 246, 0.3);
    }
    .step-label {
      color: #ffffff;
      font-weight: 600;
    }
    .step-desc {
      color: rgba(255,255,255,0.85);
    }
  `}
  
  ${props => !props.active && `
    .step-icon {
      background: rgba(255,255,255,0.08);
      border-color: rgba(255,255,255,0.25);
      color: rgba(255,255,255,0.6);
    }
    .step-label {
      color: rgba(255,255,255,0.65);
      font-weight: 500;
    }
    .step-desc {
      color: rgba(255,255,255,0.45);
    }
  `}
  
  ${props => props.isLast ? 'padding-bottom: 0;' : ''}
`;

const StepConnector = styled.div`
  position: absolute;
  left: 24px;
  top: 64px;
  width: 2px;
  height: 32px;
  background: ${props => props.completed ? '#64b5f6' : 'rgba(255,255,255,0.12)'};
  z-index: 0;
  transition: all 0.3s ease;
  
  &::after {
    content: '↓';
    position: absolute;
    bottom: -6px;
    left: -5px;
    width: 12px;
    height: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: ${props => props.completed ? '#64b5f6' : 'rgba(255,255,255,0.25)'};
    font-weight: bold;
    background: ${props => props.completed ? 'rgba(100, 181, 246, 0.15)' : 'rgba(255,255,255,0.08)'};
    border-radius: 50%;
    border: 1px solid ${props => props.completed ? 'rgba(100, 181, 246, 0.3)' : 'rgba(255,255,255,0.15)'};
  }
`;

const StepIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid;
  flex-shrink: 0;
  transition: all 0.3s ease;
  
  svg {
    font-size: 20px;
    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1));
  }
`;

const StepInfo = styled.div`
  flex: 1;
  padding-top: 4px;
`;

const StepLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
  transition: color 0.3s ease;
`;

const StepDesc = styled.div`
  font-size: 13px;
  transition: color 0.3s ease;
`;

const Copyright = styled.div`
  color: rgba(255,255,255,0.6);
  font-size: 12px;
  margin-top: auto;
  padding-top: 40px;
  position: relative;
  z-index: 1;
  font-weight: 400;
`;

const MainContent = styled.div`
  flex: 1;
  background: #f5f7fa;
  padding: 40px 60px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  height: 100vh;
  
  @media (max-width: 1200px) {
    padding: 32px 48px;
  }
  
  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`;

const StepIndicator = styled.div`
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 6px;
  letter-spacing: 0.3px;
`;

const Title = styled.h1`
  font-size: 32px;
  color: #0f172a;
  margin: 0 0 10px 0;
  font-weight: 700;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const Sub = styled.p`
  color: #64748b;
  font-size: 15px;
  margin: 0 0 16px 0;
  line-height: 1.6;
`;

const StepContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
`;

const Section = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 40px;
  width: 100%;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 100%;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
  
  @media (max-width: 768px) {
    padding: 24px 20px;
    border-radius: 12px;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px 28px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Row2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px 28px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Row3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px 28px;
  margin-bottom: 20px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Field = styled.div`
  min-width: 0;
  max-width: 100%;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 10px;
  letter-spacing: 0.2px;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 13px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    background: #ffffff;
  }
`;

const Select = styled.select`
  width: 100%;
  min-width: 0;
  padding: 13px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  cursor: pointer;
  font-family: inherit;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23475569' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-width: 0;
  padding: 13px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  box-sizing: border-box;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  
  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    background: #ffffff;
  }
`;

const BtnRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  gap: 12px;
  flex-shrink: 0;
  padding-top: 20px;
  border-top: 1.5px solid #e2e8f0;
  position: relative;
  z-index: 1;
`;

const Btn = styled.button`
  padding: 13px 32px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  font-size: 15px;
  border: none;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;
  min-width: 120px;
  
  &.primary {
    background: #3b82f6;
    color: #ffffff;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  }
  
  &.primary:hover:not(:disabled) {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
  
  &.primary:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  }
  
  &.secondary {
    background: #f8fafc;
    color: #475569;
    border: 1.5px solid #e2e8f0;
  }
  
  &.secondary:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
  
  &.secondary:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorP = styled.p`
  color: #dc2626;
  font-size: 14px;
  margin: 0 0 16px 0;
  font-weight: 500;
  position: relative;
  z-index: 1;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '⚠';
    font-size: 16px;
  }
`;

const InfoText = styled.p`
  color: #475569;
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  padding: 20px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
`;

const ServiceList = styled.ul`
  margin: 16px 0 0;
  padding: 16px 16px 16px 32px;
  max-height: 280px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #334155;
  line-height: 1.5;
`;

export default function OnboardingGarage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    garageName: '',
    displayName: '',
    ownerName: '',
    supportPhone: '',
    email: '',
    yearEst: '',
    description: '',
    // address broken into components
    area: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    openingTime: '',
    closingTime: '',
    services: ''
  });

  const loadDraft = useCallback(() => { try { const raw = localStorage.getItem(DRAFT_KEY); if (raw) { const d = JSON.parse(raw); setForm((f) => ({ ...f, ...d.form })); if (d.step) setStep(d.step); } } catch (_) {} }, []);
  useEffect(() => { loadDraft(); }, [loadDraft]);
  useEffect(() => {
    if (!user) return;
    const displayEmail = getProfileDisplayEmail(user);
    const hasPhone = !!(user.phone && String(user.phone).trim());
    setForm((f) => ({
      ...f,
      email: displayEmail ? (f.email || displayEmail) : f.email,
      supportPhone: hasPhone ? (f.supportPhone || user.phone || '') : f.supportPhone
    }));
  }, [user?.id, user?.email, user?.phone]);
  const saveDraft = useCallback(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, form })); }, [step, form]);
  useEffect(() => { const t = setInterval(saveDraft, 45000); return () => clearInterval(t); }, [saveDraft]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const garageDetails = user?.garageDetails || {};
  const expandedServices = useMemo(() => {
    const fromClusters = previewGarageServices({
      companyType: garageDetails.garageTier || garageDetails.companyType || garageDetails.sellerCategory,
      selectedClusters: garageDetails.selectedClusters,
      fuelSpecialistTypes: garageDetails.fuelSpecialistTypes,
    });
    if (fromClusters.length) return fromClusters;
    const raw = garageDetails.services || form.services;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      return raw.split(/[\n,;|•]+/).map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }, [garageDetails, form.services]);

  const handleSubmit = async () => {
    setError('');
    if (!form.garageName?.trim()) { setError('Garage name is required'); return; }
    setLoading(true);
    try {
      const updatedUser = await authService.completeOnboarding({
        role: USER_ROLES.GARAGE,
        garageDetails: {
          ...garageDetails,
          ...form,
          garageName: form.garageName,
          displayName: form.displayName || form.garageName,
          services: expandedServices.length ? expandedServices : form.services,
          servicesFromClusters: Boolean((garageDetails.selectedClusters || []).length),
        },
      });
      localStorage.removeItem(DRAFT_KEY);
      if (updatedUser) {
        updateUser?.({ ...updatedUser, role: USER_ROLES.GARAGE, onboardingComplete: true });
      }
      navigate('/garage/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const getStepTitle = () => {
    const titles = {
      1: 'Garage Identity',
      2: 'Location & Hours',
      3: 'Services',
      4: 'Bank & Agreement'
    };
    return titles[step] || 'Garage Onboarding';
  };

  const getStepDescription = () => {
    const descs = {
      1: 'Tell us about your garage to get started.',
      2: 'Provide your garage location and operating hours.',
      3: 'Review services registered from your cluster selection.',
      4: 'Complete banking details and agreements.'
    };
    return descs[step] || '';
  };

  return (
    <Wrap className="onboarding-light-page">
      <Sidebar>
        <Logo>
          <div className="logo-s">
            <div className="s-letter"></div>
            <div className="s-accent"></div>
            <div className="s-decoration"></div>
          </div>
        </Logo>
        <ProgressSteps>
          {STEPS.map((stepItem, index) => {
            const Icon = stepItem.icon;
            const isActive = step === index + 1;
            const isCompleted = step > index + 1;
            const isLast = index === STEPS.length - 1;
            return (
              <React.Fragment key={index}>
                <ProgressStep active={isActive} isLast={isLast}>
                  <StepIcon className="step-icon">
                    <Icon />
                  </StepIcon>
                  <StepInfo>
                    <StepLabel className="step-label">{stepItem.label}</StepLabel>
                    <StepDesc className="step-desc">{stepItem.desc}</StepDesc>
                  </StepInfo>
                  {!isLast && <StepConnector completed={isCompleted} />}
                </ProgressStep>
              </React.Fragment>
            );
          })}
        </ProgressSteps>
        <Copyright>All rights reserved @Sparepe</Copyright>
      </Sidebar>
      <MainContent>
        <HeaderSection>
          <StepIndicator>Step {step}/{STEPS.length}</StepIndicator>
          <Title>{getStepTitle()}</Title>
          <Sub>{getStepDescription()}</Sub>
        {error && <ErrorP>{error}</ErrorP>}
        </HeaderSection>
        <StepContent>
        {step === 1 && (
          <Section>
            <Row>
              <Field>
                <Label>Garage Name *</Label>
                <Input value={form.garageName} onChange={(e) => set('garageName', e.target.value)} placeholder="Enter garage name" />
              </Field>
              <Field>
                <Label>Display Name</Label>
                <Input value={form.displayName} onChange={(e) => set('displayName', e.target.value)} placeholder="Enter display name" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Owner Name *</Label>
                <Input value={form.ownerName} onChange={(e) => set('ownerName', e.target.value)} placeholder="Enter owner name" />
              </Field>
              <Field>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@example.com" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Mobile Number *</Label>
                <Input type="tel" value={form.supportPhone} onChange={(e) => set('supportPhone', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" />
              </Field>
              <Field>
                <Label>Year Established</Label>
                <Input value={form.yearEst} onChange={(e) => set('yearEst', e.target.value)} placeholder="YYYY" />
              </Field>
            </Row>
            <Row>
              <Field style={{ gridColumn: '1 / -1' }}>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Tell us about your garage" rows={4} />
              </Field>
            </Row>
          </Section>
        )}
        {step === 2 && (
          <Section>
            <Row>
              <Field>
                <Label>Area / Locality *</Label>
                <Input value={form.area} onChange={(e) => set('area', e.target.value)} placeholder="Area / locality" />
              </Field>
              <Field>
                <Label>City *</Label>
                <Input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="City" />
              </Field>
              <Field>
                <Label>State *</Label>
                <Input value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="State" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Country</Label>
                <Input value={form.country} onChange={(e) => set('country', e.target.value)} placeholder="Country" />
              </Field>
              <Field>
                <Label>Pincode *</Label>
                <Input value={form.pincode} onChange={(e) => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="Pincode" />
              </Field>
            </Row>
            <Row>
              <Field>
                <Label>Opening Time *</Label>
                <Input type="time" value={form.openingTime} onChange={(e) => set('openingTime', e.target.value)} />
              </Field>
              <Field>
                <Label>Closing Time *</Label>
                <Input type="time" value={form.closingTime} onChange={(e) => set('closingTime', e.target.value)} />
              </Field>
            </Row>
          </Section>
        )}
        {step === 3 && (
          <Section>
            <InfoText>
              {expandedServices.length
                ? 'These services were auto-expanded from your registration clusters. Customers will see them in garage discovery.'
                : 'Complete partner registration with service clusters first, or list services during signup.'}
            </InfoText>
            {(garageDetails.selectedClusters || []).length > 0 ? (
              <GarageServicePreview
                companyType={garageDetails.garageTier || garageDetails.companyType || garageDetails.sellerCategory}
                selectedClusters={garageDetails.selectedClusters}
                fuelSpecialistTypes={garageDetails.fuelSpecialistTypes}
              />
            ) : expandedServices.length > 0 ? (
              <ServiceList>
                {expandedServices.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ServiceList>
            ) : (
              <Row>
                <Field style={{ gridColumn: '1 / -1' }}>
                  <Label>Core services (optional)</Label>
                  <Textarea value={form.services} onChange={(e) => set('services', e.target.value)} placeholder="List your core services" rows={4} />
                </Field>
              </Row>
            )}
            {normalizeGarageTier(garageDetails.garageTier || garageDetails.companyType) === 'custom_workshop' && (
              <InfoText style={{ marginTop: 12 }}>Custom workshop uses quote-based jobs — no fixed service menu.</InfoText>
            )}
            {normalizeGarageTier(garageDetails.garageTier || garageDetails.companyType) === 'transformer' && (
              <InfoText style={{ marginTop: 12 }}>Transformer garages use staged project intake — no instant booking menu.</InfoText>
            )}
          </Section>
        )}
        {step === 4 && (
          <Section>
            <InfoText>Bank & agreement can be completed from your dashboard.</InfoText>
          </Section>
        )}
        </StepContent>
        <BtnRow>
          <Btn type="button" className="secondary" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}>Back</Btn>
          {step < STEPS.length ? (
            <Btn type="button" className="primary" onClick={() => { saveDraft(); setStep((s) => s + 1); }}>Next</Btn>
          ) : (
            <Btn type="button" className="primary" onClick={handleSubmit} disabled={loading}>{loading ? 'Submitting...' : 'Submit'}</Btn>
          )}
        </BtnRow>
      </MainContent>
    </Wrap>
  );
}
